import React, { useMemo, useState } from 'react';
import { Board, around } from './black_hole_game';
import type { Hint } from './useSolverHints';
import './BlackHole.css';
import {useNewlySafeCells} from './useGamePresentation';
import {getAllStepCosts} from './phaseBot';

interface BoardViewProps {
    board: Board;
    onCellClick: (row: number, col: number) => void;
    hints?: Map<string, Hint>;
    hintsPending?: boolean;
    numericOverlay?: 'off' | 'margins' | 'cellpoints' | 'stepcost';
    canPlay?: boolean;
    suggestedCell?: string;
}

const BoardView: React.FC<BoardViewProps> = ({ board, onCellClick, hints, hintsPending = false, numericOverlay = 'off', canPlay = true, suggestedCell }) => {
    const newlySafe = useNewlySafeCells(board);
    const showHintValues = numericOverlay === 'margins';
    const [preview, setPreview] = useState<{ row: number, col: number, board: Board } | null>(null);
    const previewCell = preview?.board === board ? preview : null;
    const setPreviewCell = (cell: { row: number, col: number } | null) => setPreview(cell ? {...cell, board} : null);

    let blackHoleRow = -1;
    let blackHoleCol = -1;
    const suckedInCells = new Set<string>();

    const gameOver = board.isGameOver();

    if (gameOver) {
        for (let i = 0; i < board.rows; i++) {
            for (let j = 0; j <= i; j++) {
                if (board.at(i, j) === 0) {
                    blackHoleRow = i;
                    blackHoleCol = j;
                    break;
                }
            }
        }
        if (blackHoleRow !== -1 && blackHoleCol !== -1) {
            for (const cell of around(blackHoleRow, blackHoleCol, board.rows)) {
                suckedInCells.add(`${cell.row}-${cell.col}`);
            }
        }
    }

    const previewBoard = (() => {
        if (!previewCell) return board;
        try {
            return board.play(previewCell.row, previewCell.col);
        } catch {
            return board;
        }
    })();

    const stepCosts = useMemo(() => {
        if (numericOverlay !== 'stepcost' || gameOver) return null;
        return getAllStepCosts(previewBoard);
    }, [previewBoard, numericOverlay, gameOver]);

    const getCellPointInfo = (row: number, col: number) => {
        let sumFirst = 0;   // Player 1 (positive values)
        let sumSecond = 0;  // Player 2 (negative values)

        for (const neighbor of around(row, col, previewBoard.rows)) {
            const val = previewBoard.at(neighbor.row, neighbor.col);
            if (val > 0) {
                sumFirst += val;
            } else if (val < 0) {
                sumSecond += Math.abs(val);
            }
        }

        if (sumFirst > sumSecond) {
            const diff = sumFirst - sumSecond;
            return { value: -diff, playerClass: 'player1' };
        } else if (sumSecond > sumFirst) {
            const diff = sumSecond - sumFirst;
            return { value: -diff, playerClass: 'player2' };
        } else {
            return { value: 0, playerClass: 'equal' };
        }
    };

    const renderCell = (row: number, col: number) => {
        const value = board.at(row, col);
        const cellKey = `${row}-${col}`;
        const isBlackHole = gameOver && row === blackHoleRow && col === blackHoleCol;
        const isSuckedIn = gameOver && suckedInCells.has(cellKey);

        const isPreview = previewCell && previewCell.row === row && previewCell.col === col;

        let cellClass = 'empty';
        let displayValue = '';

        if (isBlackHole) {
            cellClass = 'black-hole';
        } else if (value !== 0) {
            cellClass = value > 0 ? 'player1' : 'player2';
            displayValue = Math.abs(value).toString();
        } else if (isPreview) {
            const previewVal = previewBoard.at(row, col);
            cellClass = previewVal > 0 ? 'player1 preview' : 'player2 preview';
            displayValue = Math.abs(previewVal).toString();
        }

        const lastValue = board.filled % 2 === 1 ? Math.ceil(board.filled / 2) : -board.filled / 2;
        if (board.filled > 0 && value === lastValue) cellClass += ' latest-move';

        if (newlySafe.has(cellKey)) cellClass += ' newly-safe';
        const isSuggested = !gameOver && value === 0 && suggestedCell === cellKey;
        if (isSuggested) cellClass += ' suggested-cell';

        if (isSuckedIn) {
            cellClass += ' sucked-in';
        }

        const hint = !gameOver && value === 0 && !isPreview ? hints?.get(cellKey) : undefined;
        // An empty cell still awaiting its score during a live search: show a
        // placeholder so the user sees the overlay filling in cell by cell.
        const hintPending = !gameOver && value === 0 && !isPreview && !hint && hintsPending;

        const cellPointInfo = (!gameOver && value === 0 && !isPreview && numericOverlay === 'cellpoints')
            ? getCellPointInfo(row, col)
            : null;

        const stepCostInfo = (!gameOver && value === 0 && !isPreview && numericOverlay === 'stepcost')
            ? stepCosts?.get(cellKey)
            : null;

        const numericInfo = cellPointInfo
            ? cellPointInfo
            : stepCostInfo
            ? {
                value: stepCostInfo.cost > 0 ? `+${stepCostInfo.cost}` : `${stepCostInfo.cost}`,
                playerClass: stepCostInfo.isBest ? 'best' : 'cost',
            }
            : null;

        // Detect touch device
        const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
        const requiresTouchConfirmation = isTouchDevice && (numericOverlay === 'cellpoints' || numericOverlay === 'stepcost');

        const handleCellClickInternal = () => {
            if (!canPlay || gameOver || value !== 0) {
                return;
            }

            if (requiresTouchConfirmation) {
                if (previewCell && previewCell.row === row && previewCell.col === col) {
                    onCellClick(row, col);
                    setPreviewCell(null);
                } else {
                    setPreviewCell({ row, col });
                }
            } else {
                onCellClick(row, col);
                setPreviewCell(null);
            }
        };

        const handleMouseEnter = () => {
            if (!isTouchDevice && canPlay && !gameOver && value === 0) {
                setPreviewCell({ row, col });
            }
        };

        const handleMouseLeave = () => {
            if (!isTouchDevice && previewCell && previewCell.row === row && previewCell.col === col) {
                setPreviewCell(null);
            }
        };

        return (
            <button
                type="button"
                key={cellKey}
                className={`cell ${cellClass}${!canPlay && value === 0 ? ' disabled' : ''}`}
                style={isSuckedIn ? {
                    '--swallow-x': `calc(var(--pitch) * ${blackHoleCol - blackHoleRow / 2 - col + row / 2})`,
                    '--swallow-y': `calc(var(--pitch) * ${(blackHoleRow - row) * .8660254})`,
                } as React.CSSProperties : undefined}
                tabIndex={canPlay && !gameOver && value === 0 ? 0 : -1}
                aria-label={`Row ${row + 1}, space ${col + 1}: ${isBlackHole ? 'black hole' : value === 0 ? 'empty' : `${value > 0 ? 'Blue player one' : 'Orange player two'} ${Math.abs(value)}`}${isSuggested ? '. Suggested move' : ''}${hint ? `. With perfect play: ${hint.result === 'W' ? 'win' : hint.result === 'D' ? 'draw' : 'loss'}${showHintValues ? `, final margin ${hint.value}` : ''}` : ''}${stepCostInfo ? `. Tactical step cost: ${stepCostInfo.cost}${stepCostInfo.isBest ? ' (lowest cost)' : ''}` : ''}`}
                disabled={!canPlay || gameOver || value !== 0}
                onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleCellClickInternal();
                    }
                }}
                onClick={handleCellClickInternal}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {hint
                    ? (
                        <span className={`hint hint-${hint.result.toLowerCase()}`}>
                            <span className="hint-result">{hint.result}</span>
                            {showHintValues && hint.result !== "D" && (
                                <span className="hint-value">
                                    {hint.value > 0 ? `+${hint.value}` : hint.value}
                                </span>
                            )}
                            {numericInfo && (
                                <span className={`hint-value cell-value-${numericInfo.playerClass}`}>
                                    {numericInfo.value}
                                </span>
                            )}
                        </span>
                    )
                    : hintPending
                        ? <span className="hint hint-pending" aria-label="computing hint">·</span>
                        : numericInfo
                            ? <span className={`cell-value-centered cell-value-${numericInfo.playerClass}`}>{numericInfo.value}</span>
                            : displayValue}
                {value !== 0 && <span className="piece-marker" aria-hidden="true">{value > 0 ? 'Ⅰ' : 'Ⅱ'}</span>}
            </button>
        );
    };

    const renderBoard = () => {
        const rows = [];
        for (let i = 0; i < board.rows; i++) {
            const cellsInRow = [];
            for (let j = 0; j <= i; j++) {
                cellsInRow.push(renderCell(i, j));
            }
            rows.push(<div key={i} className="board-row">{cellsInRow}</div>);
        }
        return rows;
    };

    return (
        <div className={`board${gameOver ? ' board-finished' : ''}`} style={{'--board-rows': board.rows} as React.CSSProperties} role="group" aria-label="Triangular Black Hole board">
            <svg className="board-plate" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <polygon className="board-plate-halo" points="50,0 100,100 0,100"/>
                <polygon className="board-plate-face" points="50,0 100,100 0,100"/>
            </svg>
            {renderBoard()}
        </div>
    );
};

export default BoardView;
